import { MessageCircle, Map, X, Maximize2, Minimize2, ArrowLeft } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChatCore, type ChatCoreProps } from "./ChatCore";
import { AITutorAssessmentPanel } from "./assessment/AITutorAssessmentPanel";
import { TopicPanel, type TopicSelectPayload } from "./assessment/TopicPanel";
import { useMobile } from "../hooks/useMobile";
import { API_HOST } from "../constants";
import { LAYERS } from "../constants/layers";
import type { TelegramUser } from "../types";

export type AITutorDualMode = "chat" | "roadmap";

export interface AITutorDualModePanelProps {
  user: TelegramUser | null;
  onClose: () => void;
  embedded?: boolean;
  onMinWidthChange?: (w: number) => void;
  mode: AITutorDualMode;
  onModeChange: (mode: AITutorDualMode) => void;
  chatProps: Omit<ChatCoreProps, "user" | "onClose">;
  roadmapProps?: {
    onTopicSelect?: (payload: TopicSelectPayload | null) => void;
    isMobile?: boolean;
  };
  /** Показать кнопку «Закрыть» */
  showCloseButton?: boolean;
  /** Скрыть внутренний хедер (когда страница предоставляет собственную навигацию) */
  omitHeader?: boolean;
  /** Страница /ai-tutor: колонка чата уже (~70% ширины панели), roadmap без изменений */
  narrowChatColumn?: boolean;
  /** Topic to open immediately (e.g. from navigation state) */
  initialTopic?: TopicSelectPayload | null;
  /** Called after initialTopic has been consumed */
  onInitialTopicConsumed?: () => void;
}

/**
 * Единая оболочка AI Tutor.
 * Шапка панели согласована с высотой и фоном главного хедера.
 * Тема roadmap на десктопе — центрированная модалка (портал в document.body).
 */
export function AITutorDualModePanel({
  user,
  onClose,
  embedded = false,
  onMinWidthChange,
  mode,
  onModeChange,
  chatProps,
  roadmapProps,
  showCloseButton = true,
  omitHeader = false,
  narrowChatColumn = false,
  initialTopic,
  onInitialTopicConsumed,
}: AITutorDualModePanelProps) {
  const isMobile = useMobile();
  /** Совпадает с `lg:hidden` у мобильных оверлеев курса/урока: чат сразу на весь экран, без кнопки «развернуть». */
  const [viewportBelowLg, setViewportBelowLg] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const apply = () => setViewportBelowLg(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  const [isExpanded, setIsExpanded] = useState(false);
  const layoutExpanded = viewportBelowLg || isExpanded;
  const [openTopic, setOpenTopic] = useState<TopicSelectPayload | null>(null);
  const [externalPrompt, setExternalPrompt] = useState<{ id: string; text: string } | undefined>();
  const [learnedNodes, setLearnedNodes] = useState<Set<string>>(new Set());

  const {
    embedded: chatEmbedded = embedded,
    onMinWidthChange: chatOnMinWidth,
    showCloseButton: _sc,
    hideHeader: chatHideHeader,
    externalPrompt: parentExternalPrompt,
    ...restChat
  } = chatProps;
  // Эффективный externalPrompt: внутренний (от roadmap «спросить») перебивает,
  // если задан; иначе пробрасываем родительский. До правки local-state зануляло prop.
  const effectiveExternalPrompt = externalPrompt ?? parentExternalPrompt ?? null;

  /** На узких экранах крестик в строке «AI Tutor» (ChatCore); в Roadmap или при hideHeader — во внешней шапке. */
  const closeInChatHeader =
    showCloseButton && viewportBelowLg && mode === "chat" && chatHideHeader !== true;
  const closeOnOuterBar = showCloseButton && !closeInChatHeader;

  // Load persisted node progress for the user
  useEffect(() => {
    if (!user?.id) return;
    fetch(`${API_HOST}/api/users/${user.id}/roadmap/node-progress`)
      .then((r) => r.ok ? r.json() : {})
      .then((data: Record<string, string>) => {
        const learned = new Set<string>(
          Object.entries(data)
            .filter(([, v]) => v === "learned")
            .map(([k]) => k)
        );
        setLearnedNodes(learned);
      })
      .catch(() => {/* ignore */});
  }, [user?.id]);

  // Auto-open topic from navigation state
  useEffect(() => {
    if (initialTopic) {
      setOpenTopic(initialTopic);
      onInitialTopicConsumed?.();
    }
  }, [initialTopic, onInitialTopicConsumed]);

  const handleTopicSelect = useCallback((payload: TopicSelectPayload | null) => {
    setOpenTopic(payload);
    roadmapProps?.onTopicSelect?.(payload);
  }, [roadmapProps]);

  const handleCloseTopic = useCallback(() => {
    setOpenTopic(null);
    roadmapProps?.onTopicSelect?.(null);
  }, [roadmapProps]);

  useEffect(() => {
    if (!openTopic || isMobile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpenTopic(null);
        roadmapProps?.onTopicSelect?.(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openTopic, isMobile, roadmapProps]);

  const handleAskTutor = useCallback((question: string) => {
    setExternalPrompt({ id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, text: question });
    onModeChange("chat");
    setOpenTopic(null);
    roadmapProps?.onTopicSelect?.(null);
  }, [onModeChange, roadmapProps]);

  const handleMarkLearned = useCallback(async (nodeId: string) => {
    setLearnedNodes((prev) => new Set([...prev, nodeId]));
    if (!user?.id) return;
    try {
      await fetch(`${API_HOST}/api/users/${user.id}/roadmap/node/${nodeId}/progress`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "learned" }),
      });
    } catch { /* ignore */ }
  }, [user?.id]);

  const handlePostpone = useCallback(async (nodeId: string) => {
    if (!user?.id) return;
    try {
      await fetch(`${API_HOST}/api/users/${user.id}/roadmap/node/${nodeId}/progress`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "postponed" }),
      });
    } catch { /* ignore */ }
  }, [user?.id]);

  // Mobile fullscreen topic panel
  if (isMobile && openTopic) {
    return (
      <div className="fixed inset-0 z-[300] bg-white flex flex-col overflow-hidden">
        <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 border-b border-neutral-border bg-white">
          <button
            type="button"
            onClick={handleCloseTopic}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-sm text-neutral-text/70 hover:text-neutral-text hover:bg-neutral-light transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Назад
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <TopicPanel
            node={openTopic.node}
            areaLabel={openTopic.areaLabel}
            areaDescription={openTopic.areaDescription}
            user={user}
            onClose={handleCloseTopic}
            onAskTutor={handleAskTutor}
            onMarkLearned={handleMarkLearned}
            onPostpone={handlePostpone}
            learnedNodeIds={learnedNodes}
          />
        </div>
      </div>
    );
  }

  const panel = (
    <div
      className={
        layoutExpanded
          ? "fixed inset-0 z-[140] flex flex-col bg-white font-sans"
          : "flex flex-col h-full min-h-0 overflow-hidden bg-white font-sans"
      }
    >
      {/* Fallback close для режимов без внешней шапки (например, /ai-tutor с omitHeader). */}
      {omitHeader && closeOnOuterBar && (
        <div className="absolute top-3 right-3 z-20">
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-text/50 hover:text-neutral-text hover:bg-neutral-light transition-colors"
            aria-label="Закрыть панель"
            title="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/*
        Шапка панели (скрыта когда omitHeader=true):
        - высота = var(--header-height) — как у глобального хедера (стиль согласован)
        - фон = bg-neutral-bg, border-b — в духе сайта
        На десктопе fixed-панель начинается под глобальным хедером (см. AI_TUTOR_DESKTOP_TOP на страницах),
        эта полоса — первая строка панели (Чат / Roadmap / действия), а не продолжение nav-сайта.
      */}
      {!omitHeader && <div
        className="flex-shrink-0 flex items-center gap-1.5 px-3 border-b border-neutral-border bg-neutral-bg"
        style={{ height: "var(--header-height, 4.5rem)", minHeight: "var(--header-height, 4.5rem)" }}
      >
        {/* Mode tabs: icon + text, стиль как у nav в хедере */}
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <button
            type="button"
            onClick={() => onModeChange("chat")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              mode === "chat"
                ? "bg-primary/10 text-primary"
                : "text-neutral-text hover:bg-primary/10 hover:text-primary"
            }`}
            aria-pressed={mode === "chat"}
          >
            <MessageCircle className="w-4 h-4 shrink-0" />
            <span>Чат</span>
          </button>

          <button
            type="button"
            onClick={() => onModeChange("roadmap")}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              mode === "roadmap"
                ? "bg-primary/10 text-primary"
                : "text-neutral-text hover:bg-primary/10 hover:text-primary"
            }`}
            aria-pressed={mode === "roadmap"}
          >
            <Map className="w-4 h-4 shrink-0" />
            <span>Roadmap</span>
          </button>
        </div>

        {/* Разворот на весь экран — только на десктопе; до lg чат уже на весь экран */}
        {!viewportBelowLg && (
          <button
            type="button"
            onClick={() => setIsExpanded((v) => !v)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-text/50 hover:text-neutral-text hover:bg-neutral-light transition-colors flex-shrink-0"
            title={isExpanded ? "Свернуть" : "Развернуть на весь экран"}
            aria-label={isExpanded ? "Свернуть" : "Развернуть на весь экран"}
          >
            {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        )}

        {/* Close — всегда доступен во внешней шапке, включая полноэкранный режим и roadmap */}
        {closeOnOuterBar && (
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-neutral-text/50 hover:text-neutral-text hover:bg-neutral-light transition-colors flex-shrink-0"
            aria-label="Закрыть панель"
            title="Закрыть"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>}

      {/* Body: без absolute — чтобы колонка чата и шапка ChatCore стабильно получали высоту во flex */}
      <div className="flex flex-1 min-h-0 flex-col overflow-hidden min-w-0">
          {mode === "chat" ? (
            <div
              className={
                narrowChatColumn
                  ? "flex-1 min-h-0 min-w-0 w-full max-w-[70%] mx-auto flex flex-col overflow-hidden"
                  : "flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden"
              }
            >
              <ChatCore
                user={user}
                onClose={onClose}
                embedded={chatEmbedded}
                onMinWidthChange={chatOnMinWidth ?? onMinWidthChange}
                {...restChat}
                hideHeader={chatHideHeader === true}
                externalPrompt={effectiveExternalPrompt}
                showCloseButton={closeInChatHeader}
              />
            </div>
          ) : (
            <div className="flex-1 min-h-0 min-w-0 flex flex-col overflow-hidden">
              <AITutorAssessmentPanel
                user={user}
                omitHeader
                isMobile={roadmapProps?.isMobile}
                onTopicSelect={handleTopicSelect}
              />
            </div>
          )}
      </div>

      {/* Десктоп: содержимое темы roadmap — модалка по центру экрана */}
      {openTopic &&
        !isMobile &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 flex items-center justify-center bg-black/50 p-3 sm:p-6"
            style={{ zIndex: LAYERS.roadmapTopicModal }}
            onClick={handleCloseTopic}
            role="presentation"
          >
            <div
              className="w-full max-w-2xl h-[min(85vh,720px)] max-h-[85vh] flex flex-col rounded-2xl shadow-2xl bg-white border border-neutral-border overflow-hidden"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="roadmap-topic-dialog-title"
            >
              <TopicPanel
                node={openTopic.node}
                areaLabel={openTopic.areaLabel}
                areaDescription={openTopic.areaDescription}
                user={user}
                onClose={handleCloseTopic}
                onAskTutor={handleAskTutor}
                onMarkLearned={handleMarkLearned}
                onPostpone={handlePostpone}
                learnedNodeIds={learnedNodes}
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );

  // Fullscreen mode рендерим в portal, чтобы гарантированно перекрывать глобальный header.
  if (layoutExpanded && typeof document !== "undefined") {
    return createPortal(panel, document.body);
  }

  return panel;
}
